import React, { useEffect, useRef, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Container,
  Skeleton,
  Alert,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../utils/AuthContext";
import {
  cardOutline,
  fonts,
  headingFonts,
  mainButtonStyles,
  noOutline,
  primaryButtonStyles,
  semiBoldFonts,
} from "../../Styles/Styles";
import SVGIcon from "./SVGIcon";

const defaultLinks = [
  { title: "HOME", slug: "/" },
  { title: "ADS", slug: "/ads" },
  { title: "SERVICES", slug: "/services" },
  { title: "BLOG", slug: "https://blog.listed.ky/" },
  { title: "ABOUT", slug: "/about" },
  { title: "CONTACT", slug: "/contact" },
  { title: "GET STARTED", slug: "/get-started" },
];

const loggedInLinks = [
  { title: "HOME", slug: "/" },
  { title: "ADS", slug: "/ads" },
  { title: "SERVICES", slug: "/services" },
  { title: "BLOG", slug: "https://blog.listed.ky/" },
  { title: "ABOUT", slug: "/about" },
  { title: "CONTACT", slug: "/contact" },
  { title: "DASHBOARD", slug: "/dashboard" },
];

const userMenu = [
  { slug: "/dashboard", label: "Dashboard" },
  { slug: "/inbox", label: "Inbox" },
  { slug: "/notifications", label: "Notifications" },
  { slug: "/my-ads", label: "My Ads" },
  { slug: "/offers", label: "My Offers" },
  { slug: "/my-favorites", label: "My Favorites" },
  { slug: "/profile", label: "Profile" },
  { slug: "/my-services", label: "Business Profile" },
];

const superAdminMenu = [
  { slug: "/admin/ads-management", label: "Ads Management" },
  { slug: "/admin/users-management", label: "Users Management" },
  { slug: "/admin/categories-management", label: "Categories Management" },
  { slug: "/admin/searches-management", label: "Searches Management" },
  { slug: "/admin/services-management", label: "Services Management" },
  { slug: "/admin/experts-management", label: "Service Providers Management" },
  { slug: "/admin/deleted-ads-management", label: "Deleted Ads Management" },
  { slug: "/admin/users-ids-management", label: "User IDs Management" },
  { slug: "/admin/contact-form", label: "Contact Form Management" },
  { slug: "/admin/subscribers", label: "Subscribers Management" },
  { slug: "/admin/reports", label: "Reports Management" },
];

export default function NewNavbar() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isLoadingNavbar, setIsLoadingNavbar] = useState(true);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [categoriesError, setCategoriesError] = useState(null);
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl);
  const { isLoggedIn, logout, user } = useAuth();
  const isMounted = useRef(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      setIsLoadingCategories(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/categories`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch categories");
        }
        const data = await response.json();
        setCategories(data);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategoriesError(
          "Unable to load categories. Please try again later."
        );
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  const handlePostAd = () => {
    navigate("/post-ad");
  };

  const handleLogout = async () => {
    await logout();
    setLoading(true);
    setTimeout(() => {
      if (isMounted.current) {
        navigate("/get-started");
        setLoading(false);
      }
    }, 2000);
  };

  const handleMenuOpen = (event) => {
    if (!isLoggedIn) {
      navigate("/get-started");
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoadingNavbar(false);
    }, 1500); // ⏳ 1 second skeleton, adjust as needed

    return () => clearTimeout(timer);
  }, []);

  const navLinks = isLoggedIn ? loggedInLinks : defaultLinks;

  return (
    <AppBar
      position="static"
      sx={{
        borderBottom: "1px solid #e0e0e0", // Light gray border
        boxShadow: "none",
        backgroundColor: "#fff",
        color: "#000",
        padding: "10px 0px",
      }}
    >
      <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
        {/* Left Side: Logo */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Link to="/">
            <img
              src="/Listed.ky-Logo-clipped.png"
              alt="Logo"
              style={{ height: "50px", marginRight: "20px" }}
            />
          </Link>

          {/* Menu Items (Hidden on small screens) */}
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: "20px" }}>
            {isLoadingNavbar
              ? Array.from(new Array(7)).map((_, index) => (
                  <Skeleton
                    key={index}
                    variant="rectangular"
                    width={80}
                    height={30}
                    sx={{ borderRadius: "8px" }}
                  />
                ))
              : navLinks.map((page) => (
                  <Link key={page.title} to={page.slug}>
                    <Button
                      sx={{
                        ...semiBoldFonts,
                        letterSpacing: 2,
                        color: "#000",
                        "&:focus, &:active": {
                          outline: "none",
                          color: "var(--primary-color)",
                        },
                        "&:hover": {
                          color: "var(--primary-color)",
                          backgroundColor: "#fff",
                        },
                      }}
                    >
                      {page.title}
                    </Button>
                  </Link>
                ))}
          </Box>
        </Box>

        {/* Right Side: User Icon & Post Ad Button */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "15px" }}>
          {/* User Icon */}
          <IconButton
            onClick={handleMenuOpen}
            sx={{ color: "#000", ...noOutline, ...cardOutline }}
          >
            <SVGIcon />
          </IconButton>

          {/* Dropdown Menu for User */}
          {isLoggedIn && (
            <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
              {(user.role === "superAdmin"
                ? [...userMenu, ...superAdminMenu]
                : user.role === "user"
                ? userMenu
                : []
              ).map((item) => (
                <MenuItem
                  onClick={handleMenuClose}
                  component={Link}
                  to={item.slug}
                  sx={{
                    ...fonts,
                    "&:hover": {
                      color: "var(--primary-color)",
                    },
                  }}
                >
                  {item.label}
                </MenuItem>
              ))}
              {/* Logout Button */}
              {isLoggedIn && (
                <MenuItem
                  onClick={() => {
                    handleLogout();
                    handleMenuClose();
                  }}
                >
                  <Button
                    variant="contained"
                    sx={{
                      ...primaryButtonStyles,
                      backgroundColor: "red",
                    }}
                    // fullWidth
                  >
                    LOGOUT
                  </Button>
                </MenuItem>
              )}
            </Menu>
          )}

          {/* Post Ad Button */}
          <Button
            onClick={handlePostAd}
            className="main-button-style"
            sx={{
              ...mainButtonStyles,
              display: { xs: "none", sm: "none", md: "flex" },
            }}
          >
            <span>Post Ad</span>
            <span>
              <AddIcon />
            </span>
          </Button>

          {/* Hamburger Menu (Mobile) */}
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              color: "white",
              backgroundColor: "var(--primary-color)",
              display: { xs: "flex", md: "none" },
              "&:focus, &:active": {
                outline: "none",
              },
              "&:hover": {
                color: "white",
                backgroundColor: "var(--primary-color)",
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      </Toolbar>

      {/* Mobile Menu Drawer */}
      <Drawer anchor="right" open={mobileOpen} onClose={handleDrawerToggle}>
        <Box sx={{ width: 250, p: 2 }}>
          {/* Close Button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                color: "white",
                backgroundColor: "var(--primary-color)",
                display: { xs: "flex", md: "none" },
                "&:focus, &:active": {
                  outline: "none",
                },
                "&:hover": {
                  color: "white",
                  backgroundColor: "var(--primary-color)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Mobile Menu Links */}
          <List>
            {navLinks.map((page) => (
              <ListItem
                key={page.title}
                button
                component={Link}
                to={page.slug}
                onClick={handleDrawerToggle}
                sx={{
                  color: "#000",
                  "&:focus, &:active": {
                    outline: "none",
                    color: "var(--primary-color)",
                  },
                  "&:hover": {
                    color: "var(--primary-color)",
                    backgroundColor: "#fff",
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Typography sx={{ ...headingFonts }}>
                      {page.title}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
          <Divider sx={{ mb: 3 }} />

          {/* Post Ad Button */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button
              className="main-button-style"
              onClick={() => {
                handleDrawerToggle();
                handlePostAd();
              }}
              sx={{ ...mainButtonStyles }}
            >
              <span>Post Ad</span>
              <span>
                <AddIcon />
              </span>
            </Button>
          </Box>
        </Box>
      </Drawer>
      <Divider
        sx={{
          mt: 1,
          mb: 1,
          display: { xs: "none", md: "none", lg: "block" },
          width: "100%",
          backgroundColor: "#e0e0e0",
        }}
      />
      <Container
        maxWidth="xl"
        sx={{
          display: { xs: "none", md: "none", lg: "flex" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "center", sm: "center", lg: "start" },
            flexWrap: "wrap", // Ensures responsiveness
          }}
        >
          {categoriesError && (
            <Alert severity="error" sx={{ ...fonts, mt: 3 }}>
              {categoriesError}
            </Alert>
          )}
          {isLoadingCategories
            ? // Show skeletons when loading
              [...Array(5)].map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  width={100}
                  height={30}
                  sx={{
                    borderRadius: "4px",
                    margin: "5px",
                  }}
                />
              ))
            : // Show categories when loaded
              categories.map((category) => (
                <Link key={category.label} to={category.slug}>
                  <Button
                    sx={{
                      fontFamily: "ClashGrotesk-Medium",
                      letterSpacing: 1,
                      color: "#1a1e23",
                      textTransform: "capitalize",
                      display: "block",
                      "&:focus, &:active": {
                        outline: "none",
                        color: "var(--primary-color)",
                      },
                      "&:hover": {
                        color: "var(--primary-color)",
                        backgroundColor: "#fff",
                      },
                    }}
                  >
                    {category.label}
                  </Button>
                </Link>
              ))}
        </Box>
      </Container>
    </AppBar>
  );
}
